import { formatTime12 } from './clock'
import { matchupSideText } from './coachName'
import { externalOpponentLabel } from './externalMatchLabel'
import { gameNumberText } from './gameNumber'

/** The stored values one copied game line is built from. Pass them as stored:
 *  the formatter applies the coach's surname itself
 *  (through `matchupSideText`), so a caller must not pre-format either. */
export interface GameCopyInput {
  gameNum?: string | number | null
  /** `YYYY-MM-DD`. */
  date?: string | null
  /** `HH:MM`, 24-hour, as stored. */
  time?: string | null
  fieldName?: string | null
  home?: string | null
  homeCoach?: string | null
  visiting?: string | null
  visitingCoach?: string | null
  /** An Area/Section home fixture's opponent; set only when `visiting` is not one of our teams. */
  externalOpponent?: string | null
}

/** `2026-10-11` → `10/11/2026`, split by hand so no time zone can move the
 *  day. Anything else is returned as stored. The one US date for a game line,
 *  shared by the copy line and the coach portal's game line. */
export function usDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso
}

/** One plain-text line about a game, for pasting into an email or a message:
 *  `#6880 - 10/11/2026, 9:00 AM @ Victory Park 3 B10-15 (Estrada) vs. B10-26 (Brown)`.
 *
 *  Every screen that offers a copy builds the line here, so one game copies the
 *  same line wherever it is listed (owner, 2026-10-05). A missing piece is left
 *  out; no date drops the time with it. A cancelled game copies the same line.
 *  Returns "" for a game with no team on either side and no fixture opponent.
 *
 *  ⚠️ `vs.` with a period, unlike `matchupText`'s `vs`. This calls
 *  `matchupSideText` per side rather than changing `matchupText`, which the
 *  emails share. */
export function gameCopyLine(g: GameCopyInput): string {
  const opponent = g.externalOpponent?.trim()
  if (!g.home && !g.visiting && !opponent) return ''

  const visitingSide = opponent ? externalOpponentLabel(opponent) : matchupSideText(g.visiting, g.visitingCoach)
  const matchup = `${matchupSideText(g.home, g.homeCoach)} vs. ${visitingSide}`

  const date = g.date ? usDate(g.date) : ''
  const time = g.time ? formatTime12(g.time) : ''
  const when = date && time ? `${date}, ${time}` : date
  const field = g.fieldName?.trim() ? `@ ${g.fieldName.trim()}` : ''

  const num = gameNumberText(g.gameNum)
  const body = [when, field, matchup].filter(Boolean).join(' ')
  return num ? `#${num} - ${body}` : body
}
